<script lang="ts">
	import { marked } from 'marked';
	import DOMPurify from 'isomorphic-dompurify';

	let {
		content,
		className = '',
		restrictedMode = false,
	} = $props<{
		content: string;
		className?: string;
		restrictedMode?: boolean;
	}>();

	let renderedHtml = $state('');

	// Configure marked to handle HTML
	marked.use({
		async: false,
		pedantic: false,
		gfm: true,
		breaks: true,
	});

	$effect(() => {
		if (content) {
			try {
				// Render markdown to HTML
				const rawHtml = marked(content, {
					breaks: true,
					gfm: true,
				}) as string;

				// Sanitize the HTML based on mode
				if (restrictedMode) {
					// Restricted mode for product descriptions - only basic text formatting
					renderedHtml = DOMPurify.sanitize(rawHtml, {
						ALLOWED_TAGS: [
							'p',
							'br',
							'strong',
							'b',
							'em',
							'i',
							'u',
							'ul',
							'ol',
							'li',
							'h3',
							'h4',
							'h5',
							'h6',
							'blockquote',
							'del',
							'ins',
							'code',
							'pre',
						],
						ALLOWED_ATTR: [], // No attributes allowed in restricted mode
						KEEP_CONTENT: true, // Keep text content even if tags are removed
					});
				} else {
					// Normal mode for admin content - allow more tags
					renderedHtml = DOMPurify.sanitize(rawHtml, {
						ADD_TAGS: ['center'],
						ADD_ATTR: ['align'],
					});
				}
			} catch (error) {
				console.error('Error rendering markdown:', error);
				// Fall back to displaying sanitized content
				renderedHtml = DOMPurify.sanitize(content);
			}
		} else {
			renderedHtml = '';
		}
	});
</script>

<!-- eslint-disable-next-line svelte/no-at-html-tags -->
<div class="markdown-content {className}">{@html renderedHtml}</div>

<style>
	.markdown-content {
		line-height: var(--lg-leading-relaxed);
		color: var(--lg-text-primary);
	}

	/* Headers */
	.markdown-content :global(h1) {
		font-size: var(--lg-text-3xl);
		font-weight: var(--lg-font-bold);
		margin-bottom: var(--lg-space-4);
		margin-top: var(--lg-space-8);
		color: var(--lg-text-primary);
		border-bottom: 2px solid var(--lg-border-primary);
		padding-bottom: var(--lg-space-2);
	}

	.markdown-content :global(h1:first-child) {
		margin-top: 0;
	}

	.markdown-content :global(h2) {
		font-size: var(--lg-text-xl);
		font-weight: var(--lg-font-semibold);
		margin-bottom: var(--lg-space-3);
		margin-top: var(--lg-space-6);
		color: var(--lg-text-primary);
	}

	.markdown-content :global(h3) {
		font-size: var(--lg-text-lg);
		font-weight: var(--lg-font-semibold);
		margin-bottom: var(--lg-space-2);
		margin-top: var(--lg-space-4);
		color: var(--lg-text-secondary);
	}

	.markdown-content :global(h4) {
		font-size: var(--lg-text-lg);
		font-weight: var(--lg-font-semibold);
		margin-bottom: var(--lg-space-2);
		margin-top: var(--lg-space-4);
		color: var(--lg-text-muted);
	}

	/* Paragraphs */
	.markdown-content :global(p) {
		margin-bottom: var(--lg-space-4);
	}

	.markdown-content :global(p:last-child) {
		margin-bottom: 0;
	}

	/* Lists */
	.markdown-content :global(ul),
	.markdown-content :global(ol) {
		margin-bottom: var(--lg-space-4);
		padding-left: var(--lg-space-6);
	}

	.markdown-content :global(ul) {
		list-style-type: disc;
	}

	.markdown-content :global(ol) {
		list-style-type: decimal;
	}

	.markdown-content :global(li) {
		margin-bottom: 0.25rem;
	}

	.markdown-content :global(li > ul),
	.markdown-content :global(li > ol) {
		margin-top: 0.25rem;
		margin-bottom: 0.25rem;
	}

	/* Links */
	.markdown-content :global(a) {
		color: var(--lg-primary);
		text-decoration: underline;
		transition: var(--lg-transition-colors);
	}

	.markdown-content :global(a:hover) {
		color: var(--lg-primary-hover);
	}

	/* Text formatting */
	.markdown-content :global(strong) {
		font-weight: 700;
	}

	.markdown-content :global(em) {
		font-style: italic;
	}

	.markdown-content :global(del) {
		text-decoration: line-through;
		opacity: 0.7;
	}

	.markdown-content :global(ins) {
		text-decoration: underline;
		background-color: var(--lg-warning-light);
		padding: var(--lg-space-1) var(--lg-space-1);
	}

	/* Code */
	.markdown-content :global(code) {
		background-color: var(--lg-bg-secondary);
		padding: var(--lg-space-1) var(--lg-space-1);
		border-radius: var(--lg-radius-sm);
		font-family: var(--lg-font-mono);
		font-size: var(--lg-text-sm);
		color: var(--lg-error);
	}

	.markdown-content :global(pre) {
		background-color: var(--lg-gray-900);
		color: var(--lg-text-inverse);
		padding: var(--lg-space-4);
		border-radius: var(--lg-radius-lg);
		overflow-x: auto;
		margin-bottom: var(--lg-space-4);
		font-family: var(--lg-font-mono);
		font-size: var(--lg-text-sm);
	}

	.markdown-content :global(pre code) {
		background-color: transparent;
		padding: 0;
		color: inherit;
		border-radius: 0;
	}

	.markdown-content :global(kbd) {
		background-color: var(--lg-gray-700);
		color: var(--lg-gray-50);
		padding: var(--lg-space-1) var(--lg-space-2);
		border-radius: var(--lg-radius-sm);
		font-family: var(--lg-font-mono);
		font-size: var(--lg-text-xs);
		border: 1px solid var(--lg-border-primary);
		box-shadow: var(--lg-shadow-sm);
	}

	/* Blockquotes */
	.markdown-content :global(blockquote) {
		border-left: 4px solid var(--lg-border-primary);
		margin: var(--lg-space-4) 0;
		padding: var(--lg-space-2) var(--lg-space-4);
		background-color: var(--lg-bg-secondary);
		font-style: italic;
		color: var(--lg-text-muted);
	}

	.markdown-content :global(blockquote p) {
		margin-bottom: 0;
	}

	/* Horizontal rules */
	.markdown-content :global(hr) {
		border: none;
		border-top: 1px solid var(--lg-border-primary);
		margin: var(--lg-space-8) 0;
	}

	/* Tables */
	.markdown-content :global(table) {
		border-collapse: collapse;
		width: 100%;
		margin-bottom: var(--lg-space-4);
		border: 1px solid var(--lg-border-primary);
		border-radius: var(--lg-radius-lg);
		overflow: hidden;
	}

	.markdown-content :global(th),
	.markdown-content :global(td) {
		border: 1px solid var(--lg-border-primary);
		padding: var(--lg-space-3);
		text-align: left;
	}

	.markdown-content :global(th) {
		background-color: var(--lg-bg-secondary);
		font-weight: var(--lg-font-semibold);
		color: var(--lg-text-primary);
	}

	.markdown-content :global(tr:nth-child(even)) {
		background-color: var(--lg-bg-secondary);
	}

	/* Images */
	.markdown-content :global(img) {
		max-width: 100%;
		height: auto;
		margin: 1rem 0;
	}

	.markdown-content :global(img[align='right']) {
		float: right;
		margin: 0 0 1rem 1rem;
	}

	.markdown-content :global(img[align='left']) {
		float: left;
		margin: 0 1rem 1rem 0;
	}

	.markdown-content :global(img[align='center']) {
		display: block;
		margin: 1rem auto;
	}

	/* Center tag support */
	.markdown-content :global(center) {
		text-align: center;
		display: block;
	}

	/* Clear floats after content */
	.markdown-content::after {
		content: '';
		display: table;
		clear: both;
	}

	/* Mobile responsiveness */
	@media (max-width: 640px) {
		.markdown-content :global(h1) {
			font-size: 1.5rem;
		}

		.markdown-content :global(h2) {
			font-size: 1.25rem;
		}

		.markdown-content :global(pre) {
			padding: 0.75rem;
			font-size: 0.8rem;
		}

		.markdown-content :global(table) {
			font-size: 0.875rem;
		}

		.markdown-content :global(th),
		.markdown-content :global(td) {
			padding: 0.5rem;
		}

		.markdown-content :global(img[align='right']),
		.markdown-content :global(img[align='left']) {
			float: none;
			margin: 1rem auto;
			max-width: 100%;
		}
	}
</style>
